import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TablePagination
} from '@mui/material';
import { Bolt, Check, Close, Refresh, History } from '@mui/icons-material';
import { getDevicesUpdateStatus, getDeviceUpdateLogs } from '../../services/deviceAppUpdateService';
import apiService from '../../services/apiService';
import ForceCheckModal from './ForceCheckModal';
import DeviceHealthBadge from '../DeviceHealthBadge';

export default function DevicesStatusTable() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [notice, setNotice] = useState(null);
  const [forcingDeviceId, setForcingDeviceId] = useState(null);
  const [confirmForceDeviceId, setConfirmForceDeviceId] = useState(null);
  const [forcingAll, setForcingAll] = useState({ running: false, sent: 0, total: 0, failed: 0 });
  const [forceCheckModalOpen, setForceCheckModalOpen] = useState(false);
  const [forceCheckModalDevice, setForceCheckModalDevice] = useState(null);
  const [forceCheckAllModalOpen, setForceCheckAllModalOpen] = useState(false);
  const [devicesHealth, setDevicesHealth] = useState({});

  const [logsOpen, setLogsOpen] = useState(false);
  const [logsDevice, setLogsDevice] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0); // Reset pagina quando cambia filtro
    loadDevices();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDevices = async () => {
    try {
      setLoading(true);
      setError(null);
      setNotice(null);
      
      const filters = statusFilter !== 'all' ? { status: statusFilter } : {};
      const data = await getDevicesUpdateStatus(filters);
      
      setDevices(data.devices);
      setSummary(data.summary);
      setActiveVersion(data.activeVersion);

      // Carica health per i dispositivi visibili
      if (data.devices.length > 0) {
        const deviceIds = data.devices.map(d => d.deviceId).filter(Boolean);
        if (deviceIds.length > 0) {
          const healthData = await apiService.getBulkDevicesHealth(deviceIds);
          setDevicesHealth(healthData);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendForceUpdateCheck = async (deviceId) => {
    const result = await apiService.sendWebSocketCommand(deviceId, {
      event: 'force_update_check',
      data: {
        source: 'admin-webapp',
        requestedAt: new Date().toISOString(),
        forceCheck: true
      }
    });
    return result;
  };

  const handleForceDevice = async (deviceId) => {
    try {
      setForcingDeviceId(deviceId);
      setNotice(null);

      const result = await sendForceUpdateCheck(deviceId);
      if (result.success) {
        setNotice({ severity: 'success', message: '✅ Comando inviato: il device eseguirà subito il check aggiornamenti' });
      } else {
        setNotice({ severity: 'error', message: `❌ Errore invio comando: ${result.error || 'Unknown error'}` });
      }
    } catch (err) {
      setNotice({ severity: 'error', message: `❌ Errore invio comando: ${err.message}` });
    } finally {
      setForcingDeviceId(null);
    }
  };

  const runWithConcurrency = async (items, worker, concurrency = 5) => {
    const results = new Array(items.length);
    let index = 0;
    const runners = new Array(concurrency).fill(null).map(async () => {
      while (index < items.length) {
        const currentIndex = index++;
        results[currentIndex] = await worker(items[currentIndex], currentIndex);
      }
    });
    await Promise.all(runners);
    return results;
  };

  const handleForceAll = async () => {
    if (forcingAll.running) return;

    try {
      setNotice(null);
      setForcingAll({ running: true, sent: 0, total: 0, failed: 0 });

      // Fetch a larger set than the default (100) so “tutti i device” is closer to reality.
      const filters = {
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        limit: 5000
      };
      const data = await getDevicesUpdateStatus(filters);
      const deviceIds = (data.devices || []).map(d => d.deviceId).filter(Boolean);

      if (deviceIds.length === 0) {
        setNotice({ severity: 'info', message: 'Nessun device trovato per inviare il comando' });
        return;
      }

      setForcingAll({ running: true, sent: 0, total: deviceIds.length, failed: 0 });

      let sent = 0;
      let failed = 0;

      await runWithConcurrency(
        deviceIds,
        async (deviceId) => {
          try {
            const result = await sendForceUpdateCheck(deviceId);
            if (!result.success) {
              failed += 1;
            }
          } catch {
            failed += 1;
          } finally {
            sent += 1;
            setForcingAll({ running: true, sent, total: deviceIds.length, failed });
          }
        },
        6
      );

      if (failed > 0) {
        setNotice({ severity: 'warning', message: `Comandi inviati: ${deviceIds.length - failed}/${deviceIds.length}. Offline/errore: ${failed}` });
      } else {
        setNotice({ severity: 'success', message: `✅ Comandi inviati a ${deviceIds.length} device` });
      }
    } catch (err) {
      setNotice({ severity: 'error', message: `❌ Errore invio massivo: ${err.message}` });
    } finally {
      setForcingAll(prev => ({ ...prev, running: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'up-to-date': return 'success';
      case 'outdated': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'up-to-date': return '✅';
      case 'outdated': return '⏳';
      case 'failed': return '❌';
      default: return '❓';
    }
  };

  const openLogs = async (device) => {
    setLogsDevice(device);
    setLogsOpen(true);
    setLogs([]);
    setLogsError(null);

    if (!device?.deviceId) return;

    try {
      setLogsLoading(true);
      const data = await getDeviceUpdateLogs(device.deviceId, 10);
      setLogs(data.logs || []);
    } catch (err) {
      setLogsError(err.message);
    } finally {
      setLogsLoading(false);
    }
  };

  const refreshLogs = async () => {
    if (!logsDevice?.deviceId) return;
    try {
      setLogsLoading(true);
      setLogsError(null);
      const data = await getDeviceUpdateLogs(logsDevice.deviceId, 10);
      setLogs(data.logs || []);
    } catch (err) {
      setLogsError(err.message);
    } finally {
      setLogsLoading(false);
    }
  };

  if (loading && devices.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Stato Distribuzione Dispositivi</Typography>
          {activeVersion && (
            <Typography variant="body2" color="text.secondary">
              Versione Attiva: {activeVersion.versionName} ({activeVersion.versionCode})
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="all">Tutti i Dispositivi</MenuItem>
            <MenuItem value="up-to-date">✅ Aggiornati</MenuItem>
            <MenuItem value="outdated">⏳ Da Aggiornare</MenuItem>
            <MenuItem value="failed">❌ Falliti</MenuItem>
          </Select>
          <Button
            variant="contained"
            size="small"
            startIcon={<Bolt />}
            onClick={() => setForceCheckAllModalOpen(true)}
            disabled={forcingAll.running}
          >
            Forza check (tutti)
          </Button>
          <IconButton onClick={loadDevices}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      {forcingAll.running && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Invio comando in corso: {forcingAll.sent}/{forcingAll.total} (errori: {forcingAll.failed})
        </Alert>
      )}

      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="h4">{summary.total}</Typography>
            <Typography variant="body2" color="text.secondary">Dispositivi Totali</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, bgcolor: 'success.light' }}>
            <Typography variant="h4">{summary.upToDate}</Typography>
            <Typography variant="body2">Aggiornati</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, bgcolor: 'warning.light' }}>
            <Typography variant="h4">{summary.outdated}</Typography>
            <Typography variant="body2">Da Aggiornare</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, bgcolor: 'error.light' }}>
            <Typography variant="h4">{summary.failed}</Typography>
            <Typography variant="body2">Falliti</Typography>
          </Paper>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table
          size="small"
          sx={{
            '& .MuiTableCell-root': { px: 1, py: 0.75 },
            '& .MuiTableCell-head': { fontWeight: 700, whiteSpace: 'nowrap' }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Totem</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Locale</TableCell>
              <TableCell>Versione Attuale</TableCell>
              <TableCell>Versione Target</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell align="center">Health</TableCell>
              <TableCell align="center">Forza Check</TableCell>
              <TableCell>Ultimo Tentativo</TableCell>
              <TableCell>Ultimo Stato</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    Nessun dispositivo trovato
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              devices
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((device) => (
                <TableRow key={device.deviceId}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {device.deviceLabel || 'Totem'}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {device.setupCode}
                    </Typography>
                  </TableCell>
                  <TableCell>{device.tenantName || '-'}</TableCell>
                  <TableCell>{device.venueName || '-'}</TableCell>
                  <TableCell>
                    {device.currentVersionName || 'N/A'}
                    <Typography variant="caption" display="block" color="text.secondary">
                      ({device.currentVersion})
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {device.targetVersionName}
                    <Typography variant="caption" display="block" color="text.secondary">
                      ({device.targetVersion})
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${getStatusIcon(device.status)} ${device.status}`}
                      color={getStatusColor(device.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <DeviceHealthBadge
                      health={devicesHealth[device.deviceId]}
                      onClick={() => device.deviceId && navigate(`/device-health/${device.deviceId}`)}
                      showLabel={false}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {confirmForceDeviceId === device.deviceId ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Conferma invio comando">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleForceDevice(device.deviceId)}
                              disabled={forcingAll.running || forcingDeviceId === device.deviceId}
                              color="success"
                            >
                              <Check fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Annulla">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setConfirmForceDeviceId(null)}
                              disabled={forcingAll.running || forcingDeviceId === device.deviceId}
                              color="inherit"
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Tooltip title="Forza check aggiornamenti">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setForceCheckModalDevice(device);
                              setForceCheckModalOpen(true);
                            }}
                            disabled={forcingAll.running || forcingDeviceId === device.deviceId}
                          >
                            <Bolt fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {device.lastUpdateAttempt
                      ? new Date(device.lastUpdateAttempt).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell
                    onClick={() => device.deviceId && openLogs(device)}
                    onKeyDown={(e) => {
                      if (!device.deviceId) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openLogs(device);
                      }
                    }}
                    role={device.deviceId ? 'button' : undefined}
                    tabIndex={device.deviceId ? 0 : -1}
                    sx={{
                      cursor: device.deviceId ? 'pointer' : 'default',
                      userSelect: 'none',
                      '&:hover': device.deviceId ? { backgroundColor: 'action.hover' } : undefined
                    }}
                  >
                    <Tooltip title={device.deviceId ? 'Clicca per vedere ultimi 10 status update' : 'Log non disponibili'}>
                      <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                            {device.lastUpdateStatus || '-'}
                          </Typography>
                          {device.lastError && (
                            <Typography variant="caption" display="block" color="error">
                              {device.lastError}
                            </Typography>
                          )}
                        </Box>

                        <History
                          fontSize="small"
                          sx={{
                            mt: 0.25,
                            opacity: device.deviceId ? 0.9 : 0.35
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={devices.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
        />
      </TableContainer>

      <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Ultimi status update (10)
          {logsDevice?.deviceLabel ? ` — ${logsDevice.deviceLabel}` : ''}
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Setup code: {logsDevice?.setupCode || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Device ID: {logsDevice?.deviceId || '-'}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {logsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLogsError(null)}>
              {logsError}
            </Alert>
          )}

          {logsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quando</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Da → A</TableCell>
                  <TableCell>Errore</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        Nessun log disponibile.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.timestamp ? new Date(l.timestamp).toLocaleString() : '-'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={l.status || '-'} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {l.fromVersionCode} → {l.toVersionCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={l.errorMessage ? 'error' : 'text.secondary'}>
                          {l.errorMessage || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={refreshLogs} disabled={logsLoading || !logsDevice?.deviceId} startIcon={<Refresh />}>
            Aggiorna
          </Button>
          <Button onClick={() => setLogsOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Check Modal - Singolo Dispositivo */}
      <ForceCheckModal
        open={forceCheckModalOpen}
        onClose={() => {
          setForceCheckModalOpen(false);
          setForceCheckModalDevice(null);
        }}
        onConfirm={() => handleForceDevice(forceCheckModalDevice?.deviceId)}
        deviceId={forceCheckModalDevice?.deviceId}
        deviceInfo={forceCheckModalDevice}
        isAll={false}
      />

      {/* Force Check Modal - Tutti i Dispositivi */}
      <ForceCheckModal
        open={forceCheckAllModalOpen}
        onClose={() => setForceCheckAllModalOpen(false)}
        onConfirm={() => handleForceAll()}
        deviceInfo={{ targetVersionName: activeVersion?.versionName }}
        deviceCount={devices.length}
        isAll={true}
      />
    </>
  );
}
